import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Accessibility, MessageCircle, Plus, Minus, Eye, Type, X } from "lucide-react";

type FontMode = "default" | "sans" | "serif";

const FixedActionButtons = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [fontSize, setFontSize] = useState(0);
  const [highContrast, setHighContrast] = useState(false);
  const [fontMode, setFontMode] = useState<FontMode>("default");
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  // Apply font size
  useEffect(() => {
    const base = 100 + fontSize * 12.5;
    document.documentElement.style.fontSize = `${base}%`;
    return () => { document.documentElement.style.fontSize = ""; };
  }, [fontSize]);

  // Apply high contrast
  useEffect(() => {
    document.documentElement.classList.toggle("a11y-high-contrast", highContrast);
  }, [highContrast]);

  // Apply font override
  useEffect(() => {
    document.documentElement.classList.remove("a11y-font-sans", "a11y-font-serif");
    if (fontMode === "sans") document.documentElement.classList.add("a11y-font-sans");
    if (fontMode === "serif") document.documentElement.classList.add("a11y-font-serif");
  }, [fontMode]);

  const resetAll = () => {
    setFontSize(0);
    setHighContrast(false);
    setFontMode("default");
  };

  const hasChanges = fontSize !== 0 || highContrast || fontMode !== "default";

  return (
    <>
      {/* WhatsApp - bottom right */}
      <motion.a
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1, duration: 0.3 }}
        href="https://wa.me/972508258206"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 right-4 z-50 w-12 h-12 rounded-full bg-green-600 shadow-lg flex items-center justify-center hover:bg-green-700 transition-colors"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-6 h-6 text-white" />
      </motion.a>


      {/* Accessibility - bottom left */}
      <div ref={menuRef} className="fixed bottom-4 left-4 z-50">
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute bottom-14 left-0 w-64 bg-card border border-border rounded-2xl shadow-xl p-4 space-y-4"
              dir="rtl"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-foreground text-sm font-bold">הגדרות נגישות</h3>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="סגור תפריט נגישות"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Font Size */}
              <div className="space-y-2">
                <label className="text-muted-foreground text-xs font-medium flex items-center gap-1.5">
                  <Type className="w-3.5 h-3.5" />
                  גודל טקסט
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setFontSize((s) => Math.max(s - 1, -2))}
                    disabled={fontSize <= -2}
                    className="w-9 h-9 rounded-lg border border-border bg-background flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-30"
                    aria-label="הקטן טקסט"
                  >
                    <Minus className="w-4 h-4 text-foreground" />
                  </button>
                  <span className="flex-1 text-center text-foreground text-sm font-semibold">
                    {fontSize === 0 ? "רגיל" : `${fontSize > 0 ? "+" : ""}${fontSize}`}
                  </span>
                  <button
                    onClick={() => setFontSize((s) => Math.min(s + 1, 4))}
                    disabled={fontSize >= 4}
                    className="w-9 h-9 rounded-lg border border-border bg-background flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-30"
                    aria-label="הגדל טקסט"
                  >
                    <Plus className="w-4 h-4 text-foreground" />
                  </button>
                </div>
              </div>

              {/* High Contrast */}
              <div className="space-y-2">
                <label className="text-muted-foreground text-xs font-medium flex items-center gap-1.5">
                  <Eye className="w-3.5 h-3.5" />
                  ניגודיות גבוהה
                </label>
                <button
                  onClick={() => setHighContrast((v) => !v)}
                  className={`w-full py-2 rounded-lg text-sm font-semibold border transition-colors ${
                    highContrast
                      ? "bg-foreground text-background border-foreground"
                      : "bg-background text-foreground border-border hover:bg-muted"
                  }`}
                >
                  {highContrast ? "✓ מופעל" : "כבוי"}
                </button>
              </div>

              {/* Font Family */}
              <div className="space-y-2">
                <label className="text-muted-foreground text-xs font-medium">גופן</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {([
                    { key: "default" as FontMode, label: "רגיל" },
                    { key: "sans" as FontMode, label: "Sans" },
                    { key: "serif" as FontMode, label: "Serif" },
                  ]).map((f) => (
                    <button
                      key={f.key}
                      onClick={() => setFontMode(f.key)}
                      className={`py-2 rounded-lg text-xs font-semibold border transition-colors ${
                        fontMode === f.key
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-foreground border-border hover:bg-muted"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reset */}
              {hasChanges && (
                <button
                  onClick={resetAll}
                  className="w-full text-center text-xs text-primary hover:underline font-medium pt-1"
                >
                  איפוס הגדרות
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.1, duration: 0.3 }}
          onClick={() => setMenuOpen((v) => !v)}
          className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-colors ${
            hasChanges
              ? "bg-primary text-primary-foreground"
              : "bg-muted/90 backdrop-blur-md border border-border text-muted-foreground hover:bg-muted"
          }`}
          aria-label="נגישות"
        >
          <Accessibility className="w-6 h-6" />
        </motion.button>
      </div>
    </>
  );
};

export default FixedActionButtons;
